import React from "react";
import { useTheme } from "@/contexts/ThemeContext";

const LOGO_URL = "/brand/inviktus-logo.png";
const LOGO_WHITE_URL = "/brand/inviktus-logo-white.png";

/**
 * Inviktus brand mark. Renders the official PNG logo (face silhouette in
 * blue→purple gradient + INVIKTUS wordmark). On dark surfaces, pass
 * `tone="light"` to render an inverted wordmark using the gradient
 * (the supplied PNG already has a transparent / white-friendly background).
 *
 * Props:
 *  - showWord   : if false, only renders the face glyph (no wordmark)
 *  - size       : height in px of the rendered logo (the PNG keeps its
 *                 own aspect ratio so width is auto)
 *  - tone       : "auto" | "light" — used in the optional fallback wordmark.
 *  - className  : passthrough container classes.
 */
export const InviktusLogo = ({
  className = "",
  showWord = true,
  size = 36,
  tone = "auto",
}) => {
  const dark = useTheme()?.mode === "dark";
  return (
    <div
      className={`inline-flex shrink-0 items-center gap-2 ${className}`}
      aria-label="Inviktus"
      data-testid="inviktus-logo"
    >
      <img
        src={dark ? LOGO_WHITE_URL : LOGO_URL}
        alt="Inviktus"
        style={{ height: size, width: "auto" }}
        className="block max-w-none shrink-0 select-none object-contain"
        draggable={false}
      />
      {/* The PNG already includes the wordmark; `showWord` here just
          toggles whether we visually crop to the glyph only. When false,
          we replace the bitmap with a square crop via CSS so only the
          gradient face shows in small contexts (favicon-like). */}
      {!showWord && (
        <span className="sr-only">Inviktus</span>
      )}
      {/* Optional text fallback when image fails — hidden by default */}
      <span
        className={`hidden font-display font-bold tracking-[0.18em] ${
          dark ? "text-white" : "text-ink"
        }`}
        style={{ fontSize: size * 0.5 }}
      >
        INVIKTUS
      </span>
    </div>
  );
};

export default InviktusLogo;
